/**
 * Datas da entrada (review r2 robustez, M1): só ISO 8601, dia que existe no calendário e ano entre
 * 1900 e 2100. `Date.parse` e `new Date(texto)` aceitam "December 31, 9999", "12/31/9999" e
 * "Jan 1 1000" e corrigem "2026-02-30" para 2 de março em silêncio; nada disso entra.
 */
export const ANO_MINIMO = 1900;
export const ANO_MAXIMO = 2100;
export const MENSAGEM_FAIXA = `Data inválida ou fora da faixa de ${ANO_MINIMO} a ${ANO_MAXIMO}`;

const RE_DIA = /^(\d{4})-(\d{2})-(\d{2})$/;
const RE_MES = /^(\d{4})-(\d{2})$/;
const RE_DATA_HORA = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.\d{1,9})?)?(Z|[+-]\d{2}:?\d{2})?$/i;

/** O dia existe no calendário e o ano está na faixa aceita. */
export function diaValido(ano: number, mes: number, dia: number): boolean {
  if (ano < ANO_MINIMO || ano > ANO_MAXIMO || mes < 1 || mes > 12 || dia < 1) return false;
  const d = new Date(Date.UTC(ano, mes - 1, dia));
  return d.getUTCMonth() === mes - 1 && d.getUTCDate() === dia;
}

/** AAAA-MM-DD existente e na faixa. */
export function ehDataIso(v: unknown): v is string {
  if (typeof v !== 'string') return false;
  const m = RE_DIA.exec(v);
  return !!m && diaValido(Number(m[1]), Number(m[2]), Number(m[3]));
}

/** AAAA-MM na faixa. */
export function ehMesIso(v: unknown): v is string {
  if (typeof v !== 'string') return false;
  const m = RE_MES.exec(v);
  return !!m && diaValido(Number(m[1]), Number(m[2]), 1);
}

/**
 * Lê data e hora ISO 8601 (separador T ou espaço, segundos e fração opcionais, fuso Z ou ±HH:MM).
 * Sem fuso, só quando `exigeFuso` é falso (a hora fica no fuso do processo, como `new Date`).
 * Devolve null para qualquer outro formato, dia inexistente, hora impossível ou ano fora da faixa.
 */
export function lerDataHoraIso(v: unknown, opcoes: { exigeFuso?: boolean } = {}): Date | null {
  if (typeof v !== 'string') return null;
  const m = RE_DATA_HORA.exec(v);
  if (!m) return null;
  if (!diaValido(Number(m[1]), Number(m[2]), Number(m[3]))) return null;
  if (Number(m[4]) > 23 || Number(m[5]) > 59 || (m[6] !== undefined && Number(m[6]) > 59)) return null;
  if (opcoes.exigeFuso && !m[7]) return null;
  if (m[7] && !/^z$/i.test(m[7])) {
    const f = m[7].replace(':', '');
    if (Number(f.slice(1, 3)) > 14 || Number(f.slice(3, 5)) > 59) return null;
  }
  const t = Date.parse(v.replace(' ', 'T'));
  if (Number.isNaN(t)) return null;
  const ano = new Date(t).getUTCFullYear();
  return ano < ANO_MINIMO || ano > ANO_MAXIMO ? null : new Date(t);
}

/** Data (AAAA-MM-DD) ou data e hora ISO; devolve o instante (data pura em 00:00 UTC). */
export function lerDataOuDataHoraIso(v: unknown): Date | null {
  if (ehDataIso(v)) return new Date(`${v}T00:00:00Z`);
  return lerDataHoraIso(v);
}

const DIA_SAO_PAULO = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo' });

/**
 * Hoje (AAAA-MM-DD) no fuso de São Paulo, regra "datas e dia no fuso de São Paulo" (r18). Review r36 robustez (M5):
 * o dia tirado do ISO de `new Date()` é o de UTC e, das 21h à meia-noite, grava o dia seguinte.
 */
export function hojeEmSaoPaulo(agora: Date = new Date()): string {
  return DIA_SAO_PAULO.format(agora);
}
